/**
 * QRPay 充值系统 API模块
 * 封装 ICCID 充值相关接口调用
 */

import { encryptRequest } from '@/utils/qrpay-crypto.js'

// API配置 - 使用相对路径，通过反向代理访问
// 开发环境: 通过 vue.config.js 代理到后端服务器
// 生产环境: 需要在服务器配置 Nginx 反向代理
const API_BASE_URL = '/api/fuyou'

/**
 * 发送加密请求
 * @param {string} url - 接口URL
 * @param {Object} data - 原始数据
 * @returns {Promise<Object>} - 响应结果
 */
function sendEncryptedRequest(url, data) {
  return new Promise(async (resolve, reject) => {
    try {
      console.log('========================================')
      console.log('📍 [Step 1] 准备加密数据')
      console.log('📍 原始数据:', JSON.stringify(data, null, 2))

      // 1. 加密数据
      const encryptedData = encryptRequest(data)
      console.log('📍 [Step 2] 加密完成')
      console.log('📍 encryptedData:', JSON.stringify(encryptedData, null, 2))

      // 2. 构建请求参数 - 使用相对路径走代理
      const fullUrl = `${API_BASE_URL}${url}`
      console.log('📍 [Step 3] 准备发送请求')
      console.log('📍 fullUrl:', fullUrl)
      console.log('📍 请求方法: POST')

      // 3. 使用原生 fetch 发送请求
      const response = await fetch(fullUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(encryptedData)
      })

      console.log('📍 [Step 4] 收到响应')
      console.log('📍 StatusCode:', response.status)

      // 4. 解析响应
      const result = await response.json()
      console.log('📍 响应数据:', JSON.stringify(result, null, 2))
      console.log('========================================')

      if (response.status === 200) {
        resolve(result)
      } else {
        reject({
          code: response.status,
          message: result.message || '请求失败'
        })
      }

    } catch (error) {
      console.log('📍 请求失败')
      console.error('❌ 错误信息:', error)
      console.log('========================================')
      reject({
        code: -1,
        message: error.message || '网络请求失败'
      })
    }
  })
}

/**
 * 验证ICCID
 * @param {string} iccid - ICCID号码(19-20位数字)
 * @returns {Promise<Object>} - 验证结果
 *
 * 成功响应示例:
 * {
 *   "status": 1,
 *   "message": "验证成功",
 *   "data": {
 *     "iccid": "89860123456789012345",
 *     "valid": true
 *   }
 * }
 */
export function verifyICCID(iccid) {
  return sendEncryptedRequest('/iccid/recharge/verify', {
    iccid: iccid
  })
}

/**
 * 创建充值订单
 * @param {string} iccid - ICCID号码
 * @param {string} packageCode - 套餐编码
 * @returns {Promise<Object>} - 订单信息
 */
export function createRechargeOrder(iccid, packageCode) {
  return sendEncryptedRequest('/iccid/recharge/create-order', {
    iccid: iccid,
    package_code: packageCode
  })
}

/**
 * 获取充值记录
 * @param {string} iccid - ICCID号码
 * @param {number} page - 页码（默认1）
 * @param {number} pageSize - 每页条数（默认20）
 * @returns {Promise<Object>} - 充值记录列表
 */
export function getRechargeOrders(iccid, page = 1, pageSize = 20) {
  return sendEncryptedRequest('/iccid/recharge/my-orders', {
    iccid: iccid,
    page: page,
    page_size: pageSize
  })
}
